/**
 * GoersTicketInventory — V1.1 Ticket Inventory Flow (Users Orders / Tickets / Update)
 *
 * Called by: DestinationManagement.jsx (GOERS Content tab → "Ticket Inventory" sub-tab)
 * Uses: goersApi → GET /users/orders, GET /users/tickets, POST /users/ticket
 * Flow: filter orders by date → expand an order to load its ticket numbers →
 *       set each ticket's visit (schedule) date so the customer validates on arrival.
 */

import React, { useState, useCallback } from 'react';
import {
  Box, HStack, VStack, Heading, Text, Button, Input, useToast, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Card, CardBody, Badge,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiSave } from 'react-icons/fi';
import { goersApi } from '../config/api';

const formatIDR = (num) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);

const today = () => new Date().toISOString().slice(0, 10);

const GoersTicketInventory = () => {
  const toast = useToast();
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // ticket lists + per-ticket edit state keyed by order_number / ticket_number
  const [ticketsByOrder, setTicketsByOrder] = useState({});
  const [loadingOrder, setLoadingOrder] = useState(null);
  const [visitDates, setVisitDates] = useState({});
  const [savingTicket, setSavingTicket] = useState(null);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await goersApi.get('/users/orders', { params: { start_date: startDate, end_date: endDate } });
      const items = res?.data?.data || [];
      setOrders(items);
      setTicketsByOrder({});
      toast({ title: 'Orders loaded', description: `${items.length} order(s)`, status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: 'Failed to load orders', description: err?.response?.data?.message || err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, toast]);

  const fetchTickets = useCallback(async (orderNumber) => {
    if (ticketsByOrder[orderNumber]) return; // already loaded
    setLoadingOrder(orderNumber);
    try {
      const res = await goersApi.get('/users/tickets', { params: { order_number: orderNumber } });
      const list = res?.data?.data?.tickets || [];
      setTicketsByOrder((prev) => ({ ...prev, [orderNumber]: list }));
    } catch (err) {
      toast({ title: 'Failed to load tickets', description: err?.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoadingOrder(null);
    }
  }, [ticketsByOrder, toast]);

  const saveVisitDate = useCallback(async (orderNumber, ticket) => {
    const scheduleDate = visitDates[ticket.ticket_number];
    if (!scheduleDate) {
      toast({ title: 'Pick a visit date first', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setSavingTicket(ticket.ticket_number);
    try {
      const res = await goersApi.post('/users/ticket', { ticket_number: ticket.ticket_number, schedule_date: scheduleDate });
      const updated = res?.data?.data?.schedule || scheduleDate;
      // reflect the new schedule in the loaded list
      setTicketsByOrder((prev) => ({
        ...prev,
        [orderNumber]: (prev[orderNumber] || []).map((t) =>
          t.ticket_number === ticket.ticket_number ? { ...t, schedule_date: updated } : t),
      }));
      toast({ title: 'Visit date updated', description: res?.data?.message || updated, status: 'success', duration: 4000, isClosable: true });
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.message || err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSavingTicket(null);
    }
  }, [visitDates, toast]);

  return (
    <Box>
      <HStack mb={4} justify="space-between" wrap="wrap" gap={2}>
        <Heading size="md">GOERS Ticket Inventory</Heading>
        <HStack gap={2} wrap="wrap">
          <Input type="date" size="sm" w="auto" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Text fontSize="sm" color="gray.500">to</Text>
          <Input type="date" size="sm" w="auto" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <Button leftIcon={<FiRefreshCw />} colorScheme="blue" size="sm" onClick={fetchOrders} isLoading={isLoading} loadingText="Loading...">
            Load Orders
          </Button>
        </HStack>
      </HStack>
      <Text fontSize="sm" color="gray.500" mb={4}>
        Orders generated to your GOERS inventory. Expand an order to see its ticket numbers, then set each ticket's visit date so the customer can be validated on arrival.
      </Text>

      {isLoading ? (
        <VStack py={10}><Spinner size="lg" color="blue.500" /><Text color="gray.500">Loading orders...</Text></VStack>
      ) : orders.length === 0 ? (
        <Card bg="white" borderRadius="16px" boxShadow="sm">
          <CardBody><VStack py={8} spacing={2}>
            <Text color="gray.400" fontSize="lg">No orders in range</Text>
            <Text color="gray.400" fontSize="sm">Adjust the date range and click "Load Orders".</Text>
          </VStack></CardBody>
        </Card>
      ) : (
        <Accordion allowMultiple>
          {orders.map((o) => (
            <AccordionItem key={o.order_number} border="1px solid" borderColor="gray.100" borderRadius="md" mb={2}>
              <AccordionButton onClick={() => fetchTickets(o.order_number)} _expanded={{ bg: 'blue.50' }}>
                <HStack flex="1" textAlign="left" justify="space-between" wrap="wrap" gap={2}>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="semibold" fontSize="sm">{o.order_number}</Text>
                    <Text fontSize="xs" color="gray.500">{o.created_at}</Text>
                  </VStack>
                  <HStack spacing={2} wrap="wrap">
                    {(o.tickets || []).map((t, i) => (
                      <Badge key={i} colorScheme="purple" fontSize="xs">{t.ticket_name} ×{t.qty} · {formatIDR(t.ticket_price)}</Badge>
                    ))}
                  </HStack>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                {loadingOrder === o.order_number ? (
                  <Spinner size="sm" />
                ) : (ticketsByOrder[o.order_number] || []).length === 0 ? (
                  <Text fontSize="sm" color="gray.500">No ticket numbers for this order.</Text>
                ) : (
                  <TableContainer>
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>Ticket Number</Th><Th>Ticket Name</Th><Th>Current Visit Date</Th><Th>Set Visit Date</Th></Tr></Thead>
                      <Tbody>
                        {ticketsByOrder[o.order_number].map((t) => (
                          <Tr key={t.ticket_number}>
                            <Td fontFamily="mono" fontSize="xs">{t.ticket_number}</Td>
                            <Td fontSize="sm">{t.ticket_name}</Td>
                            <Td fontSize="sm">{t.schedule_date && t.schedule_date !== '-' ? t.schedule_date : <Text as="span" color="gray.400">not set</Text>}</Td>
                            <Td>
                              <HStack>
                                <Input type="date" size="xs" w="auto" min={today()}
                                  value={visitDates[t.ticket_number] || ''}
                                  onChange={(e) => setVisitDates((prev) => ({ ...prev, [t.ticket_number]: e.target.value }))} />
                                <Button size="xs" colorScheme="green" leftIcon={<FiSave />}
                                  isLoading={savingTicket === t.ticket_number} loadingText="Saving"
                                  onClick={() => saveVisitDate(o.order_number, t)}>Save</Button>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </Box>
  );
};

export default GoersTicketInventory;
